import { useState, useMemo, useCallback, type RefObject } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { EFormRenderResponse, EFormQuestion } from './eform.types'
import QuestionTypeRenderer, { HIDDEN_TYPES, DISPLAY_ONLY_TYPES } from './question-types'
import { detectFieldType, isValidEmail } from './question-types/TextQuestion'
import type { EFormVisitQueue } from './eformQueue'
import type { EFormAutosave } from './eformAutosave'
import { isVisible, splitPages, visiblePageIndexes } from './eformLogic'
import { readApproval } from './eformAnswers'

/** Strip white/near-white background-color from HTML to fix macOS compositing artifacts. */
export function stripWhiteBg(html: string): string {
  if (!html) return html
  return html.replace(
    /background-color:\s*(white|#fff(?:fff)?|rgb\(\s*255\s*,\s*255\s*,\s*255\s*\))\s*;?/gi,
    ''
  ).replace(/\s*style="\s*"/g, '')
}

interface EFormRendererProps {
  data: EFormRenderResponse
  onSubmit: (answers: Record<number, string | string[]>) => Promise<void>
  submitting: boolean
  eformToken?: string
  visitQueue?: EFormVisitQueue
  /** Saves each answer as it is entered (partial saves); absent when the account has them off. */
  autosave?: EFormAutosave
  /**
   * FTP's hidden callbackPhone honeypot (R23): not rendered (display: none),
   * unlabelled, out of the tab order and hidden from assistive tech, so a
   * browser never autofills it. The page reads it at submit.
   */
  honeypotRef?: RefObject<HTMLInputElement>
}

function getTextFieldType(q: EFormQuestion): 'phone' | 'email' | 'text' {
  if (q.questionType !== 10) return 'text'
  return detectFieldType(q.questionContent)
}

/**
 * Why a required question is not answered yet, or null when it is (or is not
 * required / takes no answer). The one check behind both the Next/Submit
 * buttons and the error messages.
 */
function requiredError(q: EFormQuestion, val: string | string[] | undefined): string | null {
  if (HIDDEN_TYPES.has(q.questionType) || DISPLAY_ONLY_TYPES.has(q.questionType)) return null
  if (!q.isRequired) return null
  const str = Array.isArray(val) ? val.join(',') : (val ?? '')

  if (q.questionType === 18) {
    const parts = str.split('|')
    if (!parts[0]?.trim() || !parts[1]?.trim()) return 'Both first and last name are required'
    return null
  }
  if (q.questionType === 22) {
    const parts = str.split('|')
    const mask = q.customTypeId || 15 // 0 or absent → all required (backward compat)
    const missing: string[] = []
    if ((mask & 1) && !parts[0]?.trim()) missing.push('street address')
    if ((mask & 2) && !parts[2]?.trim()) missing.push('city')
    if ((mask & 4) && !parts[3]?.trim()) missing.push('state')
    if ((mask & 8) && !parts[4]?.trim()) missing.push('zip')
    return missing.length > 0 ? `Required: ${missing.join(', ')}` : null
  }
  if (q.questionType === 17) {
    // "inches|weight"; the height dropdown sends 0 when nothing is picked
    const [inches = '', weight = ''] = str.split('|')
    if (!(parseInt(inches, 10) > 0) || !weight.trim()) return 'Both height and weight are required'
    return null
  }
  if (q.questionType === 6) {
    // TextareaApproval holds [text, approved]; the text is the answer
    return readApproval(val).text.trim() === '' ? 'This field is required' : null
  }
  const fieldType = getTextFieldType(q)
  if (fieldType === 'phone') {
    const digits = str.replace(/\D/g, '')
    if (digits.length < 10) return digits.length === 0 ? 'This field is required' : 'Please enter a valid 10-digit phone number'
    return null
  }
  if (fieldType === 'email') {
    if (!str.trim()) return 'This field is required'
    return isValidEmail(str) ? null : 'Please enter a valid email address'
  }
  const isEmpty = !val || (Array.isArray(val) ? val.length === 0 : val.trim() === '')
  return isEmpty ? 'This field is required' : null
}

export default function EFormRenderer({ data, onSubmit, submitting, eformToken, visitQueue, autosave, honeypotRef }: EFormRendererProps) {
  const { survey, questions, context } = data
  const accountCountry = context?.accountCountry ?? 'US'
  // A DEMO preview walks the pages but never submits (R31); the API refuses its writes too.
  const isDemo = context?.isDemo === true || context?.patientId === 'DEMO'
  const [answers, setAnswers] = useState<Record<number, string | string[]>>(() => {
    const initial: Record<number, string> = {}
    for (const q of questions) {
      if (HIDDEN_TYPES.has(q.questionType) && q.defaultValue) {
        initial[q.orderId] = q.defaultValue
      }
      if (q.questionType === 27) {
        initial[q.orderId] = q.defaultValue || '1|1|1|1'
      }
    }
    return initial
  })
  const [errors, setErrors] = useState<Record<number, string>>({})
  const [currentPage, setCurrentPage] = useState(0)

  const pages = useMemo(() => splitPages(questions), [questions])
  // The mid-form Submit (FTP): the first type 20 is the form's final Submit,
  // labelled with its text, on the page it closes. The form ends there, so the
  // pages after it (later type 20s included) are never reached.
  const midForm = useMemo(() => {
    const breakIndex = questions.findIndex((q) => q.questionType === 20)
    if (breakIndex < 0) return null
    let pageIndex = -1
    pages.forEach((page, i) => {
      const first = page.questions[0]
      if (first && questions.indexOf(first) < breakIndex) pageIndex = i
    })
    if (pageIndex < 0) return null
    const label = (questions[breakIndex].questionContent || '').replace(/<[^>]*>/g, '').trim() || 'Submit'
    return { pageIndex, label }
  }, [questions, pages])

  // The pages the patient walks through: a page whose break the logic hides is
  // skipped, and nothing after the mid-form Submit's page is reachable.
  const visiblePages = useMemo(
    () => visiblePageIndexes(pages, answers, questions).filter((i) => midForm === null || i <= midForm.pageIndex),
    [pages, answers, questions, midForm]
  )
  const totalPages = visiblePages.length
  const nextPage = visiblePages.find((i) => i > currentPage)
  const prevPage = [...visiblePages].reverse().find((i) => i < currentPage)
  const submitLabel = midForm !== null && midForm.pageIndex === currentPage ? midForm.label : 'Submit'

  const currentQuestions = useMemo(() => {
    return (pages[currentPage]?.questions ?? []).filter((q) => isVisible(q, answers, questions))
  }, [pages, currentPage, answers, questions])

  const questionByOrderId = useMemo(() => new Map(questions.map((q) => [q.orderId, q])), [questions])

  const updateAnswer = useCallback((orderId: number, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [orderId]: value }))
    setErrors((prev) => {
      const next = { ...prev }
      delete next[orderId]
      return next
    })
    const question = questionByOrderId.get(orderId)
    if (autosave && question) autosave.change(question, value)
  }, [autosave, questionByOrderId])

  const pageErrors = useMemo(() => {
    const found: Record<number, string> = {}
    for (const q of currentQuestions) {
      const error = requiredError(q, answers[q.orderId])
      if (error) found[q.orderId] = error
    }
    return found
  }, [currentQuestions, answers])
  const allRequiredFilled = Object.keys(pageErrors).length === 0

  function validatePage(): boolean {
    setErrors(pageErrors)
    return allRequiredFilled
  }

  function handleNext(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (!validatePage()) return
    if (nextPage !== undefined) {
      setCurrentPage(nextPage)
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }))
    }
  }

  function handlePrev(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (prevPage !== undefined) {
      setCurrentPage(prevPage)
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }))
    }
  }

  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  async function handleSubmit(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (submitting || submitted) return
    if (!validatePage()) return
    setSubmitted(true)
    setSubmitError(null)
    try {
      await onSubmit(answers)
    } catch (err) {
      // Back to the Submit button with the reason, so the visitor can retry.
      setSubmitted(false)
      setSubmitError(err instanceof Error && err.message ? err.message : 'Your form could not be submitted. Please try again.')
    }
  }

  // The last visible page shows Submit, even when hidden pages follow it (FTP's updateNextButton).
  const isLastPage = nextPage === undefined
  // Position among the visible pages: the visible pages before this one, plus this one.
  const pageNumber = visiblePages.filter((i) => i < currentPage).length + 1
  const progress = totalPages > 1 ? (pageNumber / totalPages) * 100 : 100

  // Track rendered groups to avoid duplicate headers
  const renderedGroups = new Set<number>()

  // Branding colors
  const btnColor = survey.buttonColor || undefined
  const btnStyle: React.CSSProperties | undefined = btnColor
    ? { backgroundColor: btnColor.startsWith('#') ? btnColor : `#${btnColor}`, borderColor: btnColor.startsWith('#') ? btnColor : `#${btnColor}` }
    : undefined
  const progressColor = btnColor
    ? (btnColor.startsWith('#') ? btnColor : `#${btnColor}`)
    : undefined

  // Apply custom branding
  // CSSProperties has no index signature, so a custom property is not a valid
  // key on it — React passes them through at runtime, the type just cannot say
  // so. The template-literal key admits exactly the `--*` names and nothing
  // else, rather than widening the whole object with Record<string, string>.
  const brandStyle: React.CSSProperties & { [key: `--${string}`]: string } = {}
  if (survey.color1) brandStyle['--eform-primary'] = survey.color1
  if (survey.color2) brandStyle['--eform-secondary'] = survey.color2

  // Page uses a lighter shade of the card color (#fdf8ee), header/footer match the page bg
  const pageBg = '#fefcf6'
  const cardBg = '#fdf8ee'
  const fontColor = '#16386b'
  const headerFooterStyle: React.CSSProperties = { backgroundColor: pageBg, color: fontColor }
  // Use dark logo (logoUrl is darkLogo from branding)
  const displayLogoUrl = survey.logoUrl || survey.lightLogoUrl

  return (
    <form
      className="min-h-screen"
      style={{ ...brandStyle, backgroundColor: pageBg, color: fontColor }}
      onSubmit={(e) => e.preventDefault()}
    >
      {/* Honeypot (FTP's callbackPhone): a person never sees it, so anything typed into it came from a bot.
          Not rendered and unlabelled, so a browser never autofills it with the visitor's phone. */}
      <div aria-hidden="true" style={{ display: 'none' }}>
        <input
          ref={honeypotRef}
          name="callbackPhone"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>
      {/* Header */}
      <div className="border-b sticky top-0 z-10" style={headerFooterStyle}>
        <div className="max-w-2xl mx-auto px-4 py-3">
          {survey.displayLogo && displayLogoUrl && (
            <div className="flex justify-center mb-2">
              <img
                src={displayLogoUrl}
                alt="Logo"
                className="h-10 w-auto object-contain"
              />
            </div>
          )}
          {survey.pageTitle && (
            <h1 className="text-base font-semibold truncate text-center" style={{ color: fontColor }}>
              {survey.pageTitle}
            </h1>
          )}
          {/* Progress bar with page count */}
          {totalPages > 1 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(22,56,107,0.1)' }}>
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${progress}%`, backgroundColor: progressColor || fontColor }}
                />
              </div>
              <p className="text-xs shrink-0" style={{ color: fontColor, opacity: 0.7 }}>
                {pageNumber}/{totalPages}
              </p>
            </div>
          )}
          {/* Sub-page title from page break */}
          {pages[currentPage]?.pageTitle && (
            <p className="mt-2 text-sm font-medium text-center" style={{ color: fontColor, opacity: 0.85 }}>
              {pages[currentPage].pageTitle}
            </p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-4 py-6 pb-28">
        {/* Header message on first page */}
        {currentPage === 0 && survey.headerMessage && (
          <div
            className="prose prose-sm max-w-none mb-6 [&_*]:bg-transparent"
            style={{ color: fontColor }}
            dangerouslySetInnerHTML={{ __html: stripWhiteBg(survey.headerMessage) }}
          />
        )}

        {/* Questions */}
        <div className="space-y-3">
          {currentQuestions.map((q) => {
            if (HIDDEN_TYPES.has(q.questionType)) {
              return (
                <QuestionTypeRenderer
                  key={q.orderId}
                  question={q}
                  value={answers[q.orderId] ?? ''}
                  onChange={(v) => updateAnswer(q.orderId, v)}
                  eformToken={eformToken}
                  visitQueue={visitQueue}
                  accountCountry={accountCountry}
                />
              )
            }

            // Group header
            let groupHeader: React.ReactNode = null
            if (q.groupId > 0 && q.groupTitle && !renderedGroups.has(q.groupId)) {
              renderedGroups.add(q.groupId)
              groupHeader = (
                <div className="border-b pb-2 mb-4 mt-2" style={{ borderColor: 'rgba(22,56,107,0.15)' }}>
                  <h2 className="text-sm font-semibold uppercase tracking-wide" style={{ color: fontColor }}>
                    {q.groupTitle}
                  </h2>
                </div>
              )
            }

            const isDisplayOnly = DISPLAY_ONLY_TYPES.has(q.questionType)

            return (
              // Leaving a question's field saves its typed answer now (partial saves).
              <div key={q.orderId} onBlur={autosave ? () => autosave.blur(q.orderId) : undefined}>
                {groupHeader}
                <div className="rounded-xl border border-slate-200 px-4 py-3 shadow-md" style={{ backgroundColor: cardBg }}>
                  {/* DEMO-mode visibility badges — only shown in form builder preview */}
                  {q.showTo === 1 && context?.patientId === 'DEMO' && (
                    <span title="This field is only visible when the form is sent to a patient" className="inline-block text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded mb-1.5">Patients Only</span>
                  )}
                  {q.showTo === 2 && context?.patientId === 'DEMO' && (
                    <span title="This field is only visible on direct links and embeds (non-patient)" className="inline-block text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mb-1.5">Non-Patients Only</span>
                  )}
                  {/* Question label — content types (19, 23, 28) render their own content inside the component */}
                  {!isDisplayOnly && q.questionType !== 28 && q.questionContent && (
                    <div className="mb-2 flex items-baseline gap-1">
                      <div
                        className="text-sm font-medium prose prose-sm max-w-none [&>*]:m-0 [&_*]:bg-transparent"
                        style={{ color: fontColor }}
                        dangerouslySetInnerHTML={{ __html: stripWhiteBg(q.questionContent) }}
                      />
                      {q.isRequired && (
                        <span className="text-red-500 text-sm leading-none shrink-0">*</span>
                      )}
                    </div>
                  )}
                  {/* Question input */}
                  <QuestionTypeRenderer
                    question={q}
                    value={answers[q.orderId] ?? ''}
                    onChange={(v) => updateAnswer(q.orderId, v)}
                    error={errors[q.orderId]}
                    eformToken={eformToken}
                    visitQueue={visitQueue}
                    accountCountry={accountCountry}
                  />
                  {/* Instruction text (hidden for payment fields where it stores merchantId, and scroll-accept where the component renders it as the checkbox label) */}
                  {q.instructionText && q.questionType !== 21 && q.questionType !== 28 && (
                    <div
                      className="text-xs mt-2 prose prose-xs max-w-none [&>*]:m-0 [&_*]:bg-transparent"
                      style={{ color: fontColor, opacity: 0.7 }}
                      dangerouslySetInnerHTML={{ __html: stripWhiteBg(q.instructionText) }}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Sticky bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 border-t z-10" style={{ ...headerFooterStyle, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="max-w-2xl mx-auto px-8 py-3">
          <p className={`text-xs text-center mb-2 flex items-center justify-center gap-1 transition-colors ${allRequiredFilled ? 'text-green-600' : 'text-red-500'}`}>
            {allRequiredFilled
              ? <><svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>Required fields completed</>
              : <><span className="text-sm leading-none">*</span>Complete all required fields</>
            }
          </p>
          {submitError && (
            <p role="alert" className="text-xs text-center mb-2 text-red-500">{submitError}</p>
          )}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={handlePrev}
              disabled={prevPage === undefined}
              className="flex items-center gap-1.5 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ borderColor: 'rgba(22,56,107,0.2)', color: fontColor }}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            {isLastPage ? (
              isDemo ? (
                <span className="px-2 py-2.5 text-xs" style={{ color: fontColor, opacity: 0.7 }}>
                  Preview only: this form can't be submitted.
                </span>
              ) : submitted ? (
                <div className="flex items-center gap-2 px-6 py-2.5">
                  <div className="h-5 w-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  <span className="text-sm font-medium" style={{ color: fontColor }}>Submitting...</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!allRequiredFilled}
                  className="flex items-center gap-1.5 rounded-lg px-6 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={btnStyle || { backgroundColor: 'var(--color-primary, #3b82f6)' }}
                >
                  {submitLabel}
                </button>
              )
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={!allRequiredFilled}
                className="flex items-center gap-1.5 rounded-lg px-6 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={btnStyle || { backgroundColor: 'var(--color-primary, #3b82f6)' }}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  )
}
